import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  PoDisclaimer,
  PoDisclaimerGroup,
  PoModalAction,
  PoModalComponent,
  PoNotificationService,
  PoPageAction,
  PoPageFilter,
  PoPageListComponent,
  PoTableAction,
  PoTableColumn,
  PoTagType,
} from "@po-ui/ng-components";
import { MatomoTracker } from "@ngx-matomo/tracker";
import { Observable, of, Subscription, switchMap } from "rxjs";
import {
  DocumentaAiService,
  DOCUMENTA_AI_TOOL_NAME,
} from "./services/documenta-ai.service";
import { McpToolUsageEntry } from "./interfaces/mcp-tool-usage";

const EMAIL_DOMAIN_SUFFIX = "@totvs.com.br";

interface DocumentaAiFilters {
  quickSearch: string;
  username: string;
  issueKey: string;
  fromDate: string;
  toDate: string;
}

const EMPTY_FILTERS: DocumentaAiFilters = {
  quickSearch: "",
  username: "",
  issueKey: "",
  fromDate: "",
  toDate: "",
};

@Component({
  selector: "app-documenta-ai",
  templateUrl: "./documenta-ai.component.html",
  styleUrls: ["./documenta-ai.component.css"],
})
export class DocumentaAiComponent implements OnInit, OnDestroy {
  @ViewChild("poPageList", { static: true }) poPageList!: PoPageListComponent;
  @ViewChild("advancedFilterModal", { static: true })
  advancedFilterModal!: PoModalComponent;
  @ViewChild("traceLogModal", { static: true })
  traceLogModal!: PoModalComponent;

  columns: Array<PoTableColumn> = [];
  tableActions: Array<PoTableAction> = [];
  items: Array<McpToolUsageEntry> = [];
  loading: boolean = true;
  loadingShowMore: boolean = false;
  exporting: boolean = false;
  errorMessage: string = "";
  totalItems: number = 0;

  /** Item selecionado para exibição no modal de Log. */
  selectedTraceLogItem: McpToolUsageEntry | null = null;

  private currentPage: number = 1;
  private totalPages: number = 1;
  private readonly pageSize: number = 100;

  filters: DocumentaAiFilters = { ...EMPTY_FILTERS };
  advancedFilters: DocumentaAiFilters = { ...EMPTY_FILTERS };

  /** Literais do po-table: troca o texto de carregamento para "Gerando CSV..." durante a exportação. */
  get tableLiterals(): { loadingData: string } {
    return this.exporting
      ? { loadingData: "Gerando CSV, aguarde..." }
      : { loadingData: "Carregando" };
  }

  /** Desabilita a busca (rápida e avançada) enquanto a tabela carrega ou o CSV está sendo gerado. */
  get filterDisabled(): boolean {
    return this.loading || this.exporting;
  }

  /** Habilitado apenas quando o backend tem mais páginas para esta busca. */
  get showMoreDisabled(): boolean {
    return (
      this.loading ||
      this.loadingShowMore ||
      this.currentPage >= this.totalPages
    );
  }

  get subtitle(): string {
    const shown = this.filteredItems.length;
    const hasMoreToLoad = this.currentPage < this.totalPages;
    const total = hasMoreToLoad ? this.totalItems : shown;
    return `Mostrando ${shown} de ${total} registro(s)`;
  }

  /** Itens filtrados na tela: a busca rápida e o filtro de Issue recortam o que já foi carregado do servidor. */
  get filteredItems(): Array<McpToolUsageEntry> {
    let items = this.items;

    const issueKeyTerm = this.filters.issueKey.trim().toLowerCase();
    if (issueKeyTerm) {
      items = items.filter((item) =>
        item.issueKey?.toLowerCase().includes(issueKeyTerm),
      );
    }

    const term = this.filters.quickSearch.trim().toLowerCase();
    if (!term) {
      return items;
    }
    return items.filter((item) =>
      [item.username, item.email, item.issueKey]
        .filter((value): value is string => !!value)
        .some((value) => value.toLowerCase().includes(term)),
    );
  }

  readonly filter: PoPageFilter = {
    action: (term: string) => this.onQuickSearch(term),
    advancedAction: () => this.openAdvancedFilter(),
    placeholder: "Buscar por usuário, e-mail ou issue...",
  };

  disclaimerGroup: PoDisclaimerGroup = {
    title: "Filtros ativos",
    disclaimers: [],
    remove: (event) => this.onDisclaimerRemove(event),
    removeAll: () => this.onRemoveAll(),
  };

  readonly advancedFilterApplyAction: PoModalAction = {
    label: "Aplicar",
    action: () => this.applyAdvancedFilter(),
  };

  readonly advancedFilterCancelAction: PoModalAction = {
    label: "Cancelar",
    action: () => this.advancedFilterModal.close(),
  };

  readonly pageActions: Array<PoPageAction> = [
    {
      label: "Exportar CSV",
      icon: "an an-file-csv",
      action: () => this.exportCsv(),
      disabled: () => this.loading || this.exporting || this.totalItems === 0,
    },
    {
      label: "Atualizar",
      icon: "an an-arrow-clockwise",
      action: () => this.load(),
      disabled: () => this.loading,
    },
  ];

  private subscriptions: Subscription = new Subscription();

  constructor(
    private documentaAiService: DocumentaAiService,
    private poNotificationService: PoNotificationService,
    private readonly tracker: MatomoTracker,
  ) {}

  ngOnInit(): void {
    this.tracker.trackPageView("Admin > MCP > Reports > Documenta.AI");
    this.setColumns();
    this.setTableActions();
    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Recarrega a partir da primeira página, substituindo os itens exibidos. */
  load(): void {
    this.loading = true;
    this.errorMessage = "";
    this.currentPage = 1;

    this.subscriptions.add(
      this.documentaAiService
        .getUsage(
          DOCUMENTA_AI_TOOL_NAME,
          this.currentPage,
          this.pageSize,
          this.buildServerFilters(),
        )
        .subscribe({
          next: (response) => {
            this.items = response.items || [];
            this.totalItems = response.totalItems ?? this.items.length;
            this.totalPages = response.totalPages ?? 1;
            this.currentPage = response.page ?? 1;
          },
          complete: () => (this.loading = false),
          error: () => {
            this.loading = false;
            this.errorMessage =
              "Não foi possível carregar o uso da tool Documenta.AI. Tente novamente mais tarde.";
          },
        }),
    );
  }

  /** Busca a próxima página (100 registros) e acrescenta os itens aos já exibidos ("Carregar mais resultados"). */
  loadMore(): void {
    if (this.showMoreDisabled) {
      return;
    }

    this.loadingShowMore = true;
    const nextPage = this.currentPage + 1;

    this.subscriptions.add(
      this.documentaAiService
        .getUsage(
          DOCUMENTA_AI_TOOL_NAME,
          nextPage,
          this.pageSize,
          this.buildServerFilters(),
        )
        .subscribe({
          next: (response) => {
            this.items = [...this.items, ...(response.items || [])];
            this.totalItems = response.totalItems ?? this.items.length;
            this.totalPages = response.totalPages ?? this.totalPages;
            this.currentPage = response.page ?? nextPage;
          },
          complete: () => (this.loadingShowMore = false),
          error: () => {
            this.loadingShowMore = false;
            this.poNotificationService.error(
              "Não foi possível carregar mais registros. Tente novamente.",
            );
          },
        }),
    );
  }

  onQuickSearch(term: string): void {
    if (this.filterDisabled) {
      return;
    }
    this.filters.quickSearch = term;
    this.updateDisclaimers();
  }

  openAdvancedFilter(): void {
    if (this.filterDisabled) {
      return;
    }
    this.advancedFilters = { ...this.filters };
    this.advancedFilterModal.open();
  }

  /** Abre o modal com o Log (JSON) do registro, inclusive quando houve erro na publicação. */
  openTraceLog(item: McpToolUsageEntry): void {
    this.selectedTraceLogItem = item;
    this.traceLogModal.open();
  }

  closeTraceLog(): void {
    this.traceLogModal?.close();
    this.selectedTraceLogItem = null;
  }

  /** Rótulo em pt-BR do status, usado no modal de log e na exportação CSV. */
  traceLogStatusLabel(status: McpToolUsageEntry["status"]): string {
    if (status === "error") return "Erro";
    if (status === "warning") return "Alerta";
    return "Sucesso";
  }

  /** Tipo semântico do po-tag (cor) correspondente ao status. */
  traceLogStatusType(status: McpToolUsageEntry["status"]): PoTagType {
    if (status === "error") return PoTagType.Danger;
    if (status === "warning") return PoTagType.Warning;
    return PoTagType.Success;
  }

  applyAdvancedFilter(): void {
    this.filters = {
      ...this.advancedFilters,
      quickSearch: this.filters.quickSearch,
    };
    this.advancedFilterModal.close();
    this.updateDisclaimers();
    this.load();
  }

  onDisclaimerRemove({
    removedDisclaimer,
  }: {
    removedDisclaimer: PoDisclaimer;
  }): void {
    switch (removedDisclaimer.property) {
      case "quickSearch":
        this.filters.quickSearch = "";
        this.poPageList.clearInputSearch();
        break;
      case "username":
        this.filters.username = "";
        this.load();
        break;
      case "issueKey":
        this.filters.issueKey = "";
        break;
      case "period":
        this.filters.fromDate = "";
        this.filters.toDate = "";
        this.load();
        break;
    }
    this.updateDisclaimers();
  }

  onRemoveAll(): void {
    this.filters = { ...EMPTY_FILTERS };
    this.poPageList.clearInputSearch();
    this.disclaimerGroup = { ...this.disclaimerGroup, disclaimers: [] };
    this.load();
  }

  exportCsv(): void {
    if (this.exporting || this.loading) {
      return;
    }

    this.exporting = true;
    const serverFilters = this.buildServerFilters();

    this.subscriptions.add(
      this.fetchAllPages(serverFilters).subscribe({
        next: (allItems) => {
          const items = this.applyClientFilters(allItems);
          if (items.length === 0) {
            this.poNotificationService.warning(
              "Não há registros para exportar com o filtro atual.",
            );
            this.exporting = false;
            return;
          }
          this.buildAndDownloadCsv(items);
          this.exporting = false;
        },
        error: () => {
          this.exporting = false;
          this.poNotificationService.error(
            "Não foi possível exportar todos os registros. Tente novamente.",
          );
        },
      }),
    );
  }

  /** Busca todas as páginas do backend para os filtros informados, ignorando a paginação já exibida na tela. */
  private fetchAllPages(
    serverFilters: ReturnType<typeof this.buildServerFilters>,
    page: number = 1,
    accumulated: Array<McpToolUsageEntry> = [],
  ): Observable<Array<McpToolUsageEntry>> {
    return this.documentaAiService
      .getUsage(DOCUMENTA_AI_TOOL_NAME, page, this.pageSize, serverFilters)
      .pipe(
        switchMap((response) => {
          const items = [...accumulated, ...(response.items || [])];
          const totalPages = response.totalPages ?? 1;
          const currentPage = response.page ?? page;

          if (currentPage >= totalPages) {
            return of(items);
          }
          return this.fetchAllPages(serverFilters, currentPage + 1, items);
        }),
      );
  }

  /** Aplica o filtro de Issue e a busca rápida sobre o conjunto completo de itens (fora do que já foi carregado na tela). */
  private applyClientFilters(
    items: Array<McpToolUsageEntry>,
  ): Array<McpToolUsageEntry> {
    let filtered = items;

    const issueKeyTerm = this.filters.issueKey.trim().toLowerCase();
    if (issueKeyTerm) {
      filtered = filtered.filter((item) =>
        item.issueKey?.toLowerCase().includes(issueKeyTerm),
      );
    }

    const term = this.filters.quickSearch.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter((item) =>
        [item.username, item.email, item.issueKey]
          .filter((value): value is string => !!value)
          .some((value) => value.toLowerCase().includes(term)),
      );
    }

    return filtered;
  }

  private buildAndDownloadCsv(items: Array<McpToolUsageEntry>): void {
    const header = [
      "Usuário",
      "Issue",
      "Data",
      "Horário",
      "Status",
      "Log",
    ];
    const rows = items.map((item) => [
      item.email || item.username,
      item.issueKey,
      item.date,
      item.time,
      this.traceLogStatusLabel(item.status),
      item.traceLog,
    ]);

    const csvContent = [header, ...rows]
      .map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");

    // BOM (\ufeff) evita acentuação corrompida ao abrir o CSV no Excel.
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `documenta-ai-${this.buildTimestampSuffix()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    this.poNotificationService.success(
      `${items.length} registro(s) exportado(s).`,
    );
  }

  private buildServerFilters(): {
    userId?: string;
    fromTimestamp?: string;
    toTimestamp?: string;
  } {
    const serverFilters: {
      userId?: string;
      fromTimestamp?: string;
      toTimestamp?: string;
    } = {};

    if (this.filters.username.trim()) {
      serverFilters.userId = this.stripEmailDomain(this.filters.username);
    }
    if (this.filters.fromDate) {
      serverFilters.fromTimestamp = new Date(
        `${this.filters.fromDate}T00:00:00.000Z`,
      ).toISOString();
    }
    if (this.filters.toDate) {
      // Inclusivo: a API trata toTimestamp como exclusivo, então avança para o início do dia seguinte.
      const toDate = new Date(`${this.filters.toDate}T00:00:00.000Z`);
      toDate.setUTCDate(toDate.getUTCDate() + 1);
      serverFilters.toTimestamp = toDate.toISOString();
    }

    return serverFilters;
  }

  /** Aceita e-mail completo ou apenas o login; remove o domínio @totvs.com.br quando presente. */
  private stripEmailDomain(value: string): string {
    const trimmed = value.trim();
    return trimmed.toLowerCase().endsWith(EMAIL_DOMAIN_SUFFIX)
      ? trimmed.slice(0, -EMAIL_DOMAIN_SUFFIX.length)
      : trimmed;
  }

  private updateDisclaimers(): void {
    const disclaimers: Array<PoDisclaimer> = [];

    if (this.filters.quickSearch) {
      disclaimers.push({
        label: `Busca: ${this.filters.quickSearch}`,
        value: this.filters.quickSearch,
        property: "quickSearch",
      });
    }
    if (this.filters.username) {
      disclaimers.push({
        label: `Usuário: ${this.filters.username}`,
        value: this.filters.username,
        property: "username",
      });
    }
    if (this.filters.issueKey) {
      disclaimers.push({
        label: `Issue: ${this.filters.issueKey}`,
        value: this.filters.issueKey,
        property: "issueKey",
      });
    }
    if (this.filters.fromDate || this.filters.toDate) {
      const from = this.filters.fromDate || "início";
      const to = this.filters.toDate || "hoje";
      disclaimers.push({
        label: `Período: ${from} a ${to}`,
        value: `${this.filters.fromDate}|${this.filters.toDate}`,
        property: "period",
      });
    }

    this.disclaimerGroup = { ...this.disclaimerGroup, disclaimers };
  }

  private buildTimestampSuffix(): string {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  }

  private setTableActions(): void {
    this.tableActions = [
      {
        label: "Visualizar log",
        icon: "an an-file-magnifying-glass",
        action: (row: McpToolUsageEntry) => this.openTraceLog(row),
      },
    ];
  }

  private setColumns(): void {
    this.columns = [
      {
        property: "email",
        type: "string",
        label: "Usuário",
      },
      {
        property: "issueKey",
        type: "string",
        label: "Issue",
      },
      {
        property: "date",
        type: "date",
        label: "Data",
        format: "dd/MM/yyyy",
      },
      {
        property: "time",
        type: "string",
        label: "Horário",
      },
      {
        property: "status",
        type: "label",
        label: "Status",
        labels: [
          { value: "success", type: PoTagType.Success, label: "Sucesso" },
          { value: "warning", type: PoTagType.Warning, label: "Alerta" },
          { value: "error", type: PoTagType.Danger, label: "Erro" },
        ],
      },
    ];
  }
}
